import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Mail, Loader2, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PreviewWaitlist — used inside in-page "preview" experiences (Men's/Women's
 * Health categories that are honest about being early). Shows:
 *   1. A small "Coming next in this module" list (4 forthcoming capabilities)
 *   2. A calm "Notify me when this opens" email-capture form
 *
 * Posts to the existing POST /api/waitlist with a topic-specific source so
 * the team can route announcements precisely. Idempotent on duplicates
 * (server returns 409 → still treated as success in the UI).
 *
 * Tone is intentionally low-key: no "Sign up now!", no "Be the first!" —
 * users on a preview screen are exploring, not converting. We respect that.
 */
const PreviewWaitlist = ({ topic, source, upcoming = [], accentClass = 'text-gray-600' }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  // One-shot view tracking per (source, mount). Doesn't fire on rerenders.
  // The endpoint silently no-ops on failure, so we never surface errors.
  const viewSent = useRef(false);
  useEffect(() => {
    if (viewSent.current || !source) return;
    viewSent.current = true;
    axios
      .post(`${API}/api/waitlist/preview-view`, { source }, { withCredentials: true })
      .catch(() => {
        // Best-effort — analytics-only signal, never block the UI.
      });
  }, [source]);

  const handleJoin = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/waitlist`,
        { email: value, source },
        { withCredentials: true },
      );
      setJoined(true);
      // Backend returns 200 with `already_joined: true` for duplicates rather
      // than 409 — handle both shapes so the UI message stays gentle.
      if (res?.data?.already_joined) {
        toast.success("You're already on the list — we'll keep you posted.");
      } else {
        toast.success("You're on the list — we'll let you know.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not join the list. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mt-4"
      data-testid={`preview-waitlist-${source}`}
    >
      {upcoming.length > 0 && (
        <>
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3 inline-flex items-center gap-1.5">
            <Sparkles className={`h-3.5 w-3.5 ${accentClass}`} />
            Coming next in {topic}
          </p>
          <ul className="space-y-2 mb-5">
            {upcoming.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700 leading-relaxed">
                <span className={`w-1.5 h-1.5 rounded-full ${accentClass.replace('text-', 'bg-')} mt-2 shrink-0`} aria-hidden="true" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {!joined ? (
        <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
          <p className="text-sm font-semibold text-gray-900 mb-1">Want a quiet heads-up?</p>
          <p className="text-xs text-gray-600 leading-relaxed mb-3">
            We'll send a single email when {topic} opens fully — nothing else, no marketing.
          </p>
          <form onSubmit={handleJoin} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Mail className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                disabled={submitting}
                className="w-full pl-9 pr-3 h-11 text-sm rounded-xl border border-gray-200 bg-white focus:border-gray-400 focus:outline-none transition-colors"
                data-testid={`preview-waitlist-email-${source}`}
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="h-11 px-4 text-sm font-semibold rounded-xl bg-gray-900 hover:bg-black disabled:opacity-60 text-white motion-safe:transition-colors"
              data-testid={`preview-waitlist-submit-${source}`}
            >
              {submitting && <Loader2 className="h-4 w-4 inline-block animate-spin mr-1.5" />}
              Notify me
            </button>
          </form>
        </div>
      ) : (
        <div
          className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2"
          data-testid={`preview-waitlist-joined-${source}`}
        >
          <CheckCircle className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" />
          <p className="text-xs text-emerald-900 leading-relaxed">
            Thanks — you're on the list. We'll send a single email when {topic} opens.
          </p>
        </div>
      )}
    </div>
  );
};

export default PreviewWaitlist;
